import { useEffect, useState } from "react";
import { api, type AccessLogEntry } from "@/api";
import { useUI } from "@/lib/theme";

function timeAgo(iso: string, lang: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return lang === "es" ? "hace un momento" : "just now";
  if (diff < 3600) {
    const m = Math.floor(diff / 60);
    return lang === "es" ? `hace ${m} min` : `${m}m ago`;
  }
  if (diff < 86400) {
    const h = Math.floor(diff / 3600);
    return lang === "es" ? `hace ${h} h` : `${h}h ago`;
  }
  const d = Math.floor(diff / 86400);
  return lang === "es" ? `hace ${d} días` : `${d}d ago`;
}

export function AccessLog() {
  const { t, lang } = useUI();
  const [entries, setEntries] = useState<AccessLogEntry[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.accessLog()
      .then((r) => setEntries(r.entries))
      .catch(() => setError(true));
  }, []);

  if (error) {
    return <p className="py-2 text-sm text-rose-500">{lang === "es" ? "Error al cargar" : "Failed to load"}</p>;
  }

  if (entries === null) {
    return <p className="py-2 text-sm text-muted">{lang === "es" ? "Cargando…" : "Loading…"}</p>;
  }

  if (entries.length === 0) {
    return <p className="py-2 text-sm text-muted">{t("audit_empty")}</p>;
  }

  return (
    <ul className="space-y-1.5">
      {entries.map((e) => {
        const coach = [e.actor_firstname, e.actor_lastname].filter(Boolean).join(" ") ||
          (lang === "es" ? "Tu entrenador" : "Your coach");
        const actionLabel = t(`audit_action_${e.action}` as Parameters<typeof t>[0]);
        const resourceLabel = t(`audit_res_${e.resource}` as Parameters<typeof t>[0]);
        const denied = (e.status_code ?? 200) >= 400;
        return (
          <li
            key={e.id}
            className={`rounded-lg px-3 py-2 text-xs ${denied ? "bg-rose-500/8 text-rose-600 dark:text-rose-400" : "bg-panel text-fg"}`}
          >
            <span className="font-medium">{coach}</span>
            {" · "}
            <span className="text-muted">{actionLabel} {resourceLabel}</span>
            {denied && <span className="ml-1 text-rose-500/70">(denegado)</span>}
            <span className="float-right text-muted">{timeAgo(e.created_at, lang)}</span>
          </li>
        );
      })}
    </ul>
  );
}
